'use client';

import { useEffect, useRef, useCallback } from 'react';
import { cn } from '@/core/utils/cn';
import { X } from 'lucide-react';
import { useTranslation } from '@/core/i18n';

const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  side?: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  width?: string;
  className?: string;
  id?: string;
  /** Hide the header close (X) button — use when footer already has Cancel */
  hideCloseButton?: boolean;
}

const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  side = 'right',
  size,
  width,
  className,
  id,
  hideCloseButton,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        const firstFocusable = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        firstFocusable?.focus();
      });
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Escape must close the drawer regardless of where focus currently sits.
  //
  // The React onKeyDown below only fires while focus is inside the panel. That
  // breaks whenever a control inside the drawer disables itself — a disabled
  // element cannot hold focus, so the browser moves focus to <body> and Escape
  // silently stops working. A "Generate" button that disables during its
  // request is enough to trigger it, and the user is then stuck with a drawer
  // that ignores Escape for no visible reason.
  //
  // A dialog is expected to close on Escape whichever child has focus, so this
  // listens at the document while open. The onKeyDown handler stays for focus
  // trapping (Tab), and closing twice is harmless because onClose is idempotent.
  useEffect(() => {
    if (!open) return;
    const onDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [open, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'Tab' && panelRef.current) {
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose]);

  const resolvedWidth = size ? sizeStyles[size] : (width || 'max-w-md');

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} />
      )}
      <div
        ref={panelRef}
        id={id}
        // `role="dialog"` only while open.
        //
        // A closed drawer is merely `invisible` — still in the DOM, still in
        // the accessibility tree. Keeping the role on it meant every page
        // permanently exposed a dialog that a screen reader would announce and
        // a user could tab into, with no `aria-modal` because the drawer is
        // shut. A11Y-09 caught exactly that on /contact.
        //
        // `aria-hidden` + `inert` take it out of both the accessibility tree
        // and the tab order when closed, which is what "closed" should mean.
        role={open ? 'dialog' : undefined}
        aria-modal={open ? 'true' : undefined}
        aria-hidden={open ? undefined : true}
        inert={open ? undefined : true}
        aria-label={title || 'Drawer'}
        onKeyDown={handleKeyDown}
        className={cn(
          'fixed top-0 z-50 h-dvh bg-hms-card transition-transform duration-300 ease-out w-full flex flex-col overflow-hidden shadow-[-4px_0_24px_rgba(0,0,0,0.25)]',
          resolvedWidth,
          side === 'right' ? 'right-0 border-l-2 border-hms-border' : 'left-0 border-r-2 border-hms-border',
          open
            ? 'translate-x-0 visible'
            : side === 'right' ? 'translate-x-full invisible' : '-translate-x-full invisible',
          className
        )}
      >
        {(title || description) && (
          <div className="flex items-start justify-between px-6 py-4 border-b border-hms-border flex-shrink-0">
            <div className="min-w-0 flex-1">
              {title && <h2 className="text-[15px] font-semibold text-hms-text">{title}</h2>}
              {description && <p className="text-[12px] text-hms-textDim mt-0.5">{description}</p>}
            </div>
            {!hideCloseButton && (
              <button id="shared-drawer-close"
                onClick={onClose}
                aria-label={t('shared.drawer.close')}
                className="text-hms-textDim hover:text-hms-text transition-colors ml-3 mt-0.5 flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">{children}</div>

        {footer && (
          <div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-hms-border bg-hms-card">
            {footer}
          </div>
        )}
      </div>
    </>
  );
}
